import React, { useMemo, useCallback, useState, useRef } from 'react';
import { Provider } from 'react-redux';
import { createStore } from 'redux';
import {
  combineForms,
  actions,
  Form,
  getField,
  Control,
  Errors
} from 'react-redux-form';
import { get } from 'lodash';
import shortid from 'shortid';

const createComponents = ref => () => {
  return {
    Form: ({ onChange: baseOnChange, ...props }) => {
      const { forceUpdate, key, model, store } = ref.current;

      const onChange = useCallback(
        value => {
          forceUpdate();
          if (baseOnChange) baseOnChange(value);
        },
        [baseOnChange]
      );

      return (
        <Provider store={store}>
          <Form key={key} onChange={onChange} model={model} {...props} />
        </Provider>
      );
    }
  };
};

export const useForm = ({ model = 'local', initialState = {} } = {}) => {
  const [loaded, setLoaded] = useState(false);
  const [, forceUpdate] = useState();
  const valueRef = useRef();
  const fieldRef = useRef();
  const [store, dispatch, key] = useMemo(
    () => {
      const store = createStore(
        combineForms({
          [model]: initialState
        })
      );

      const dispatch = action => {
        return typeof action === 'function'
          ? action(store.dispatch, store.getState)
          : store.dispatch(action);
      };

      return [store, dispatch, shortid()];
    },
    [model]
  );

  const state = store.getState();
  fieldRef.current = getField(state, model);
  valueRef.current = get(state, model);

  const setValue = value => {
    dispatch(actions.change(model, value, { silent: true }));
    dispatch(actions.setInitial(model));
    setLoaded(true);
  };

  const save = async (client, url, item, resetPending = true) => {
    try {
      return item.id
        ? await client.put(`${url}/${item.id}`, item)
        : await client.post(url, item);
    } finally {
      if (resetPending) {
        dispatch(actions.setPending('local', false));
      }
    }
  };

  const load = async (client, url, id, defaultValue) => {
    return id === 'new' ? defaultValue : client.get(`${url}/${id}`).get('data');
  };

  const getTitle = (id, title) => {
    return id === 'new' ? `Create ${title}` : `Edit ${title}`;
  };

  const componentRef = useRef();
  componentRef.current = { forceUpdate, key, store, model };

  const components = useMemo(createComponents(componentRef), []);

  return {
    model,
    setValue,
    loaded,
    field: fieldRef.current,
    value: valueRef.current,
    store,
    dispatch,
    save,
    load,
    valueRef,
    fieldRef,
    Control,
    Errors,
    actions,
    getTitle,
    ...components,
  };
};

export default useForm;



// WEBPACK FOOTER //
// ./src/hooks/useForm.js